import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dialog } from '@microsoft/teams-js';
import { Form, FormInput, FormDropdown, FormTextArea } from '@fluentui/react-northstar';
import ErrorMessage from 'components/ErrorMessage';
import clsx from 'clsx';
import Loader from 'components/Loader';
import { useQuery } from 'react-query';
import { GET_BONUS_DATA } from 'Services/apiKeys';
import { getBonusData } from 'Services/apiFunctions';
import PropTypes from 'prop-types';

const memberLabelId = 'member-label-id';

const BonusForm = ({ authToken, setSubmitLoader }) => {
    const navigate = useNavigate();
    const [selectedUser, setSelectedUser] = useState({});
    const [selectedChannel, setSelectedChannel] = useState({});
    const [disableButton, setDisableButton] = useState(false);
    const [bonusMessage, setBonusMessage] = useState('');
    const [bonusAmount, setBonusAmount] = useState(0);
    const [members, setMembers] = useState({ data: [], isFetching: true });
    const [channels, setChannels] = useState({ data: [], isFetching: true });
    const [bonusInputLabel, setBonusInputLabel] = useState('Bonus Amount: ');
    const [error, setError] = useState({ point: false, user: false, message: false, channel: false });

    useQuery([GET_BONUS_DATA, authToken], getBonusData, {
        onError: (err) => navigate('/modals/inactive'),
        onSuccess: (data) => {
            let bonusData = data.data;
            let memberList = bonusData.users.reduce((list, member) => {
                return [...list, { header: member.userName, currency: member.userCurrency, id: member._id }];
            }, []);
            let channelList = bonusData.channels.reduce(
                (list, option) => {
                    return [
                        ...list,
                        {
                            header: `${option.groupName} -> ${option.channelName}`,
                            channelId: option.channelId,
                            groupId: option.groupId,
                        },
                    ];
                },
                [{ header: 'Send privately', content: '', id: 'sendPrivately' }]
            );

            setMembers({ data: memberList, isFetching: false });
            setChannels({ data: channelList, isFetching: false });
        },
    });

    const handleRecognizeMemberSelection = (_event, { value }) => {
        setSelectedUser(value);
        setBonusInputLabel(`Bonus Amount in ${value?.currency}: `);
    };

    const bonusAmountValidation = bonusAmount < 1 || bonusAmount > 999999;

    const validateForm = () => {
        setError({
            point: bonusAmountValidation,
            user: !selectedUser?.id,
            message: !bonusMessage,
            channel: !(selectedChannel && Object.keys(selectedChannel)?.length > 2),
        });
        return (
            bonusAmountValidation ||
            !selectedUser.id ||
            !bonusMessage ||
            !(selectedChannel && Object.keys(selectedChannel)?.length > 2)
        );
    };

    const handleSubmit = () => {
        if (validateForm()) {
            return;
        }
        setDisableButton(true);
        setSubmitLoader(true);
        const data = {
            amount: bonusAmount,
            selectedUsers: [selectedUser.id],
            message: bonusMessage,
            channel: { ...selectedChannel },
            currency: selectedUser.currency,
        };
        if (selectedChannel?.id) {
            data.channel = selectedChannel?.id;
        }
        dialog.url.submit(
            { data, startedVia: 'EW Shortcuts messaging extension', flagType: 'bonus_submit' },
            process.env.REACT_APP_TEAMS_APP_ID
        );
    };

    return (
        <Form onSubmit={handleSubmit}>
            <FormDropdown
                label={{
                    content: <div className='subtitle-4 margin-bottom-8'>Who do you want to send bonus to:</div>,
                    id: memberLabelId,
                }}
                items={members.data}
                onChange={handleRecognizeMemberSelection}
                aria-labelledby={memberLabelId}
                className={error.user ? 'red-border' : ''}
                placeholder='Type or select someone'
                loading={members.isFetching}
                loadingMessage={<Loader />}
                fluid
                search
            />
            {error.user && <ErrorMessage />}
            <FormInput
                label={<div className='subtitle-4 margin-bottom-8'>{bonusInputLabel}</div>}
                name='bonus-amount'
                id='bonus-amount'
                type='number'
                placeholder='Enter the amount you would like to award as bonus'
                fluid
                className={clsx({ 'bonus-amount-input': true, 'red-border': error.point })}
                min='1'
                onChange={(_event, { value }) => setBonusAmount(value)}
            />
            {error.point && (
                <ErrorMessage
                    {...(bonusAmountValidation ? { message: 'Please enter a valid number value (1-999999)' } : {})}
                />
            )}
            <FormTextArea
                name='recognize-modal-message'
                id='recognize-modal-message'
                label={<div className='subtitle-4 margin-bottom-8'>Message</div>}
                className={error.message ? 'red-border' : ''}
                placeholder='Write a message to go along with the bonus'
                onChange={(_event, { value }) => (value.length > 1000 ? null : setBonusMessage(value))}
                fluid
            />
            {error.message && <ErrorMessage />}
            <FormDropdown
                label={{
                    content: <div className='subtitle-4 margin-bottom-8'>Where do you want to post the message?</div>,
                    id: memberLabelId,
                }}
                clearable
                items={channels.data}
                onChange={(_event, { value }) => setSelectedChannel(value)}
                aria-labelledby={memberLabelId}
                placeholder='Type or select channel'
                className={error.channel ? 'red-border' : ''}
                loading={channels.isFetching}
                loadingMessage={<Loader />}
                fluid
                search
            />
            {error.channel && <ErrorMessage />}
            <div className='body-2'>Bonus message can be sent privately or to a public channel</div>
            <div className='modal-button-container'>
                <button
                    disabled={
                        disableButton ||
                        bonusAmountValidation ||
                        !selectedUser?.id ||
                        !bonusMessage ||
                        !(selectedChannel && Object.keys(selectedChannel)?.length > 2)
                    }
                >
                    Submit
                </button>
            </div>
        </Form>
    );
};
BonusForm.propTypes = {
    authToken: PropTypes.string,
    setSubmitLoader: PropTypes.func,
};

export default BonusForm;
